// Lightweight WebSocket client for the messenger.
//
// One socket per logged-in user covers every thread they participate in.
// Pages tell the socket which sessions they care about via `subscribe`;
// `message.new` / `typing` / `read` events are emitted back so the UI can
// refresh instantly instead of waiting for the next poll.
//
// Auto-reconnects with exponential backoff. The existing polling code stays
// in place as a safety net so a WS outage never silently breaks chat.

// Base URL is read from the same env the rest of the FE uses, then http(s)
// is swapped for ws(s).
const API_BASE_URL = process.env.REACT_APP_API_URL
    || process.env.REACT_APP_BACKEND_URL
    || (typeof window !== 'undefined' && window.__API_BASE_URL__)
    || 'http://localhost:8000';

const toWsBase = (httpBase) => {
    if (!httpBase) return '';
    if (httpBase.startsWith('https://')) return 'wss://' + httpBase.slice('https://'.length);
    if (httpBase.startsWith('http://')) return 'ws://' + httpBase.slice('http://'.length);
    return httpBase;
};

// `?portal=agent` tells the backend auth middleware to prefer the
// `access_token` cookie. On localhost both apps share an origin so without
// this hint a CRM session could pick up a buyer cookie (or vice versa). In
// production the cookies live on different subdomains and this hint is
// harmless.
//
// `?token=...` is the production-critical fallback for cross-site setups
// (CRM on one host, API on another) where browsers won't send the HttpOnly
// cookie on the WS handshake — without it the connection rejects with 4401.
// The backend consumer accepts the token from either source — see
// chatbot/consumers/auth.py::_token_from_query. Matches the same fallback
// pattern api/index.js uses for REST calls (Bearer header from jwt_access).
const buildWsUrl = () => {
    const params = new URLSearchParams({ portal: 'agent' });
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('jwt_access') : null;
    if (token) params.set('token', token);
    return `${toWsBase(API_BASE_URL)}/ws/messenger/?${params.toString()}`;
};

class MessengerSocket {
    constructor() {
        this.ws = null;
        this.listeners = new Map();
        this.subscribed = new Set();
        this.reconnectAttempt = 0;
        this.shouldReconnect = true;
        this.connecting = false;
        this.connected = false;
        this.pingTimer = null;
    }

    on(event, handler) {
        if (!this.listeners.has(event)) this.listeners.set(event, new Set());
        this.listeners.get(event).add(handler);
        return () => this.listeners.get(event)?.delete(handler);
    }

    _emit(event, payload) {
        const handlers = this.listeners.get(event);
        if (!handlers) return;
        for (const h of handlers) {
            try { h(payload); } catch (_) { /* ignore listener errors */ }
        }
    }

    connect() {
        if (this.ws || this.connecting) return;
        this.connecting = true;
        this.shouldReconnect = true;
        try {
            this.ws = new WebSocket(buildWsUrl());
        } catch (e) {
            this.connecting = false;
            this._scheduleReconnect();
            return;
        }
        this.ws.onopen = () => {
            this.connecting = false;
            this.connected = true;
            this.reconnectAttempt = 0;
            this._emit('connected', null);
            for (const sid of this.subscribed) {
                this._sendRaw({ type: 'subscribe', session_id: sid });
            }
            this.pingTimer = setInterval(() => {
                this._sendRaw({ type: 'ping' });
            }, 25000);
        };
        this.ws.onmessage = (evt) => {
            let msg;
            try { msg = JSON.parse(evt.data); } catch (_) { return; }
            if (!msg || !msg.type) return;
            this._emit(msg.type, msg);
        };
        this.ws.onclose = (evt) => {
            this.connected = false;
            this.connecting = false;
            this.ws = null;
            if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
            this._emit('disconnected', { code: evt?.code });
            if (evt && evt.code === 4401) return; // server rejected auth — don't loop
            if (this.shouldReconnect) this._scheduleReconnect();
        };
        this.ws.onerror = () => { /* onclose follows */ };
    }

    _scheduleReconnect() {
        const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempt));
        this.reconnectAttempt += 1;
        setTimeout(() => this.connect(), delay);
    }

    disconnect() {
        this.shouldReconnect = false;
        if (this.ws) {
            try { this.ws.close(); } catch (_) {}
        }
    }

    _sendRaw(obj) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
        try { this.ws.send(JSON.stringify(obj)); return true; } catch (_) { return false; }
    }

    subscribe(sessionId) {
        if (!sessionId) return;
        this.subscribed.add(sessionId);
        this._sendRaw({ type: 'subscribe', session_id: sessionId });
    }

    unsubscribe(sessionId) {
        if (!sessionId) return;
        this.subscribed.delete(sessionId);
        this._sendRaw({ type: 'unsubscribe', session_id: sessionId });
    }

    sendTyping(sessionId, typing) {
        this._sendRaw({ type: 'typing', session_id: sessionId, typing: !!typing });
    }

    markRead(sessionId, upToMessageId) {
        this._sendRaw({ type: 'read', session_id: sessionId, up_to_message_id: upToMessageId || 0 });
    }
}

const messengerSocket = new MessengerSocket();
export default messengerSocket;
